import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search } from "lucide-react";
import { api, inr } from "@/lib/api";

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        setResults(await api.products(q ? { q, limit: 6 } : { limit: 6 }));
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-modal"
          className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="mx-auto mt-20 w-[calc(100%-2rem)] max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -24, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-4">
              <Search size={18} className="text-neutral-400" />
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products — flask, watch, diffuser..."
                className="w-full bg-transparent text-base outline-none placeholder:text-neutral-400"
              />
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2" data-lenis-prevent>
              {loading && <p className="px-4 py-6 text-sm text-neutral-400">Searching...</p>}
              {!loading && results.length === 0 && (
                <p className="px-4 py-6 text-sm text-neutral-400" data-testid="search-empty">
                  No products found. Try "serum" or "backpack".
                </p>
              )}
              {results.map((p) => (
                <Link
                  key={p.slug}
                  to={`/product/${p.slug}`}
                  onClick={onClose}
                  data-testid={`search-result-${p.slug}`}
                  className="flex items-center gap-4 rounded-xl px-3 py-3 transition-colors hover:bg-neutral-50"
                >
                  <img src={p.image} alt={p.name} className="h-12 w-12 rounded-lg object-cover img-frame" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-neutral-900">{p.name}</p>
                    <p className="text-xs text-neutral-500">{p.category_name}</p>
                  </div>
                  <span className="text-sm font-semibold text-neutral-900">{inr(p.price)}</span>
                </Link>
              ))}
            </div>
            <button
              data-testid="search-view-all"
              onClick={() => {
                onClose();
                navigate(q ? `/shop?q=${encodeURIComponent(q)}` : "/shop");
              }}
              className="flex w-full items-center justify-center gap-2 border-t border-neutral-100 py-3.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
            >
              View all products <ArrowRight size={15} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
